import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { API_BASE_URL } from '../../config/api';
import { BetaTestReport, DeployTest, User } from '../../types';
import { resolveTester } from '../../utils/tester';
import { testingUrl } from '../../config/hosts';
import { firmwareTypeLabel } from './deployTestUtils';

const PRIVACY_POLICY_URL = 'https://wiki.fluoraplant.com/privacy-policy';

interface BetaHomeViewProps {
  user: User;
  onAuth: (user: User) => void;
}

const formatDate = (value?: string | Date) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const BetaHomeView: React.FC<BetaHomeViewProps> = ({ user, onAuth }) => {
  const navigate = useNavigate();
  const tester = resolveTester(user);
  const isTester = tester === 'beta';

  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [hereToHelp, setHereToHelp] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const [loading, setLoading] = useState(isTester);
  const [currentTest, setCurrentTest] = useState<DeployTest | null>(null);
  const [currentReport, setCurrentReport] = useState<BetaTestReport | null>(null);
  const [starting, setStarting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [spotsFull, setSpotsFull] = useState(false);

  const loadCurrent = useCallback(async () => {
    if (!isTester) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/beta-test-reports/current`, {
        withCredentials: true,
      });
      setCurrentTest(response.data.test || null);
      setCurrentReport(response.data.report || null);
      setLoadError('');
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not load the current test.';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [isTester]);

  useEffect(() => {
    loadCurrent();
  }, [loadCurrent]);

  useEffect(() => {
    if (isTester) return;
    let cancelled = false;
    axios.get(`${API_BASE_URL}/beta/spots`)
      .then((response) => {
        if (cancelled) return;
        const available = Number(response.data?.available);
        setSpotsFull(Number.isInteger(available) && available <= 0);
      })
      .catch(() => {
        if (!cancelled) setSpotsFull(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isTester]);

  const handleJoinBeta = async () => {
    if (joining) return;
    if (!hereToHelp) {
      setJoinError('Please confirm that you are here to help test beta releases.');
      return;
    }
    if (!privacyAccepted) {
      setJoinError('Please confirm that you have read and accepted the privacy policy.');
      return;
    }
    setJoining(true);
    setJoinError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/auth/join-beta`,
        { betaOnly: true, privacyAccepted: true, helpAgreed: true },
        { withCredentials: true },
      );
      if (response.data.user) {
        onAuth(response.data.user);
      }
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not join the beta program. Please try again.';
      setJoinError(message);
    } finally {
      setJoining(false);
    }
  };

  const handleStartTest = async () => {
    if (!currentTest || starting) return;
    setStarting(true);
    setLoadError('');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/beta-test-reports`,
        { deployTestId: currentTest._id },
        { withCredentials: true },
      );
      const testId = response.data.test?._id || currentTest._id;
      navigate(`/take/${testId}`);
    } catch (err) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : 'Could not start the test.';
      setLoadError(message);
      setStarting(false);
    }
  };

  if (tester === 'internal') {
    return (
      <div className="beta-portal-panel">
        <div className="beta-home-join">
          <h1>Internal tester</h1>
          <p>
            This account is on the internal testing track. Take internal tests
            on the Internal Testing portal.
          </p>
          <a href={testingUrl('/')} className="btn btn-primary btn-lg">
            Open Internal Testing
          </a>
        </div>
      </div>
    );
  }

  if (!isTester) {
    return (
      <div className="beta-portal-panel">
        <div className="beta-home-join">
          {spotsFull && (
            <div className="beta-landing-full-banner" role="status">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              We are at max. Please try again at a later time when a seat might become available.
            </div>
          )}
          <h1>Join Beta</h1>
          <p>
            Opt in to receive beta firmware on this account and take part in
            Fluora beta tests.
          </p>
          <div className="beta-home-disclaimer">
            <p>
              The Fluora beta is for helping us catch bugs before a release goes
              to production. By joining, you agree to test releases that may
              have issues, and you are willing to take the time to give us
              feedback so we can make the product better.
            </p>
            <label className="beta-consent-check">
              <input
                type="checkbox"
                checked={hereToHelp}
                onChange={(event) => setHereToHelp(event.target.checked)}
              />
              <span>I'm here to help!</span>
            </label>
          </div>
          <label className="beta-consent-check">
            <input
              type="checkbox"
              checked={privacyAccepted}
              onChange={(event) => setPrivacyAccepted(event.target.checked)}
            />
            <span>
              I have read and accept the{' '}
              <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
                privacy policy
              </a>
              .
            </span>
          </label>
          {joinError && (
            <div className="alert alert-danger mb-3" role="alert">
              {joinError}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={handleJoinBeta}
            disabled={joining || spotsFull || !privacyAccepted || !hereToHelp}
          >
            {joining ? 'Joining…' : 'Join Beta'}
          </button>
        </div>
      </div>
    );
  }

  const showStartCard = currentTest && currentReport?.status !== 'completed';
  const continueTest = currentReport?.status === 'in_progress';

  return (
    <div className="beta-portal-panel">
      <div className="beta-home-welcome">
        <div className="beta-portal-kicker">Fluora Beta</div>
        <h1>Welcome{user.firstName ? `, ${user.firstName}` : ''}</h1>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : showStartCard && currentTest ? (
        <div className="beta-home-test-card">
          <div className="beta-home-test-kicker">
            Current test
            {currentReport?.round ? ` · Round ${currentReport.round}` : ''}
          </div>
          <h2>{currentTest.name}</h2>
          {currentTest.createdAt && (
            <div className="beta-home-test-date">{formatDate(currentTest.createdAt)}</div>
          )}
          <div className="beta-home-test-versions">
            {currentTest.firmwareVersion && (
              <div>
                <span>Firmware</span>
                <strong>
                  v{currentTest.firmwareVersion}
                  {firmwareTypeLabel(currentTest.firmwareType)
                    ? ` (${firmwareTypeLabel(currentTest.firmwareType)})`
                    : ''}
                </strong>
              </div>
            )}
            {(currentTest.mobileAppVersionIos || currentTest.mobileAppVersion) && (
              <div>
                <span>Mobile App (iOS)</span>
                <strong>v{currentTest.mobileAppVersionIos || currentTest.mobileAppVersion}</strong>
              </div>
            )}
            {(currentTest.mobileAppVersionAndroid || currentTest.mobileAppVersion) && (
              <div>
                <span>Mobile App (Android)</span>
                <strong>v{currentTest.mobileAppVersionAndroid || currentTest.mobileAppVersion}</strong>
              </div>
            )}
          </div>
          {currentTest.overview?.trim() && (
            <div className="beta-home-test-overview">
              <span>Instructions</span>
              <p>{currentTest.overview}</p>
            </div>
          )}
          {loadError && (
            <div className="alert alert-danger" role="alert">
              {loadError}
            </div>
          )}
          <button
            type="button"
            className="btn btn-primary btn-lg beta-home-start-btn"
            onClick={handleStartTest}
            disabled={starting}
          >
            <FontAwesomeIcon icon={faPlay} className="me-2" />
            {starting
              ? 'Starting…'
              : currentReport?.round && currentReport.round > 1
                ? `${continueTest ? 'Continue' : 'Start'} Round ${currentReport.round}`
                : continueTest
                  ? 'Continue Test'
                  : 'Start Test'}
          </button>
        </div>
      ) : (
        <div className="beta-home-empty">
          {loadError ? (
            <div className="alert alert-danger mb-0" role="alert">
              {loadError}
            </div>
          ) : currentTest && currentReport?.status === 'completed' ? (
            <>
              <h2>You're all caught up</h2>
              <p>You've completed {currentTest.name}. We'll let you know when the next test is ready.</p>
            </>
          ) : (
            <>
              <h2>No active test</h2>
              <p>There isn't a beta test in progress right now. Check back soon.</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BetaHomeView;
